'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Image from 'next/image';
import Header from '@/components/common/Header';

export default function Homepage() {
  return (
    <div className="bg-[#101822] text-slate-100 font-sans antialiased overflow-x-hidden">
      <Header />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#FF6B4A] mb-8">
            <span className="material-symbols-outlined text-lg">auto_awesome</span>
            <span className="text-xs font-bold tracking-widest uppercase">Join India's largest PMM network</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight mb-8 leading-[1.1]">
            Elevating <span className="whitespace-nowrap">Product Marketing</span> in <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B4A] to-[#8B5CF6]">India, Together.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-20 leading-relaxed">
            A growth ecosystem for marketing leaders driven by cohorts, frameworks, and community.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            <div className="group p-6 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-3xl md:text-4xl font-extrabold text-[#FF6B4A] mb-1">2,000+</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Active Members</p>
            </div>
            <div className="group p-6 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-3xl md:text-4xl font-extrabold text-[#FF6B4A] mb-1">2</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cohorts Completed</p>
            </div>
            <div className="group p-6 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-3xl md:text-4xl font-extrabold text-[#FF6B4A] mb-1">500+</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Jobs Curated</p>
            </div>
            <div className="group p-6 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-3xl md:text-4xl font-extrabold text-[#FF6B4A] mb-1">100+</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Frameworks Shared</p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <section className="py-12 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-xs font-bold text-slate-500 uppercase tracking-widest mb-10">PMMs from India's fastest growing startups</p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-50 grayscale hover:grayscale-0 transition-all">
            <AppImage 
              src="/assets/images/Zomato_Logo_1-1770207512324.png" 
              alt="Zomato logo" 
              className="h-8 w-auto object-contain"
            />
            <AppImage 
              src="/assets/images/Logo-1770207668237.png" 
              alt="Swiggy logo" 
              className="h-8 w-auto object-contain"
            />
            <AppImage 
              src="/assets/images/idPlSi7Egd_1770207599057-1770207726972.png" 
              alt="CRED logo" 
              className="h-8 w-auto object-contain"
            />
            <AppImage 
              src="/assets/images/Razorpay_idFvfTBRmf_1-1770207570977.png" 
              alt="Razorpay logo" 
              className="h-8 w-auto object-contain"
            />
            <AppImage 
              src="/assets/images/PhonePe_ida2PdOXh_1-1770207923321.png" 
              alt="PhonePe logo" 
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4">Everything you need to <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B4A] to-[#8B5CF6]">grow as a PMM.</span> </h2>
            <p className="text-slate-400">Tailored resources for every stage of your career journey.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#FF6B4A]/50 transition-all group">
              <div className="w-14 h-14 bg-[#FF6B4A]/20 rounded-2xl flex items-center justify-center text-[#FF6B4A] mb-6">
                <span className="material-symbols-outlined text-3xl">school</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Education</h3>
              <p className="text-slate-400 mb-6">Learn & Upskill with battle-tested frameworks from industry veterans.</p>
              <Link href="/cohort" className="inline-flex items-center text-[#FF6B4A] font-bold gap-2 group-hover:gap-3 transition-all">
                View Details <span className="material-symbols-outlined">arrow_forward</span>
              </Link>
            </div>
            <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#8B5CF6]/50 transition-all group">
              <div className="w-14 h-14 bg-[#8B5CF6]/20 rounded-2xl flex items-center justify-center text-[#8B5CF6] mb-6">
                <span className="material-symbols-outlined text-3xl">work_history</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Career</h3>
              <p className="text-slate-400 mb-6">Find a Job at India's top product companies looking for elite PMM talent.</p>
              <Link href="/jobs-page" className="inline-flex items-center text-[#8B5CF6] font-bold gap-2 group-hover:gap-3 transition-all">
                View Details <span className="material-symbols-outlined">arrow_forward</span>
              </Link>
            </div>
            <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 transition-all group">
              <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-500 mb-6">
                <AppImage 
                  src="/assets/images/410201-PD391H-802_1-1770360314949.png" 
                  alt="WhatsApp logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-3">Network</h3>
              <p className="text-slate-400 mb-6">Connect with 2,000+ practitioners to share insights and opportunities.</p>
              <a href="https://chat.whatsapp.com/KWIZZsKbsKk61ZeWjeqOwT" className="inline-flex items-center text-blue-500 font-bold gap-2 group-hover:gap-3 transition-all">
                Join WhatsApp community <span className="material-symbols-outlined">arrow_forward</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Offline Events Section */}
      <section className="py-24 px-4 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4">Building PMM Hubs <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B4A] to-[#8B5CF6]">Across India.</span></h2>
            <p className="text-slate-400">Join active city chapters for curated mixers, workshops, and real connections in your local tech hub.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
            <div className="md:col-span-2 relative group overflow-hidden rounded-3xl">
              <AppImage
                src="/assets/images/Blr_meet-1770224777329.jpg"
                alt="Bangalore Chapter Mixer - GoPMM city meetup event with product marketing professionals networking"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="relative group overflow-hidden rounded-3xl">
              <AppImage
                src="/assets/images/meet_upzzz-1770225735656.jpg"
                alt="GTM Strategy Deep-dive workshop - GoPMM educational session on go-to-market strategies"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="relative group overflow-hidden rounded-3xl">
              <AppImage
                src="/assets/images/1760421894080-1770225595647.jpeg"
                alt="Swiggy Campaign Presentation - Workshop session featuring Swiggy marketing campaign case study and strategy presentation"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="md:col-span-2 relative group overflow-hidden rounded-3xl">
              <AppImage
                src="/assets/images/1745217454994-1770225862008.jpeg"
                alt="India PMM Summit 2024 - Annual GoPMM conference with product marketing leaders and industry experts"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-24 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="bg-[#FF6B4A]/10 text-[#FF6B4A] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 inline-block">Hiring Now</span>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">Your next role is waiting.</h2>
            <p className="text-lg text-slate-400 mb-8 max-w-lg">We partner with India's top unicorns and global startups to bridge the gap between talented PMMs and the roles they deserve.</p>
            <Link href="/jobs-page" className="px-8 py-4 bg-[#FF6B4A] text-white rounded-2xl font-bold shadow-xl shadow-[#FF6B4A]/20 hover:scale-105 transition-all flex items-center gap-2 inline-flex">
              Explore Job Board <span className="material-symbols-outlined">open_in_new</span>
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-[#FF6B4A]/20 blur-[100px] rounded-full"></div>
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/20 p-6 rounded-3xl transform rotate-2 hover:rotate-0 transition-all duration-500">
              <div className="flex justify-between items-start mb-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="material-symbols-outlined text-3xl">rocket_launch</span>
                </div>
                <span className="bg-[#25D366]/10 text-[#25D366] px-3 py-1 rounded-lg text-xs font-bold">$40k - $65k</span>
              </div>
              <h4 className="text-2xl font-bold mb-1">Senior Product Marketer</h4>
              <p className="text-slate-400 mb-4">Unicorn Fintech • Remote</p>
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="px-3 py-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full text-[10px] font-semibold">GTM Strategy</span>
                <span className="px-3 py-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full text-[10px] font-semibold">Product Analytics</span>
                <span className="px-3 py-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full text-[10px] font-semibold">Copywriting</span>
              </div>
              <div className="w-full h-12 bg-white/10 rounded-xl flex items-center justify-center font-bold text-sm cursor-pointer hover:bg-white/20 transition-colors">Apply with GoPMM Profile</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cohort Promotion Section */}
      <section className="py-24 px-4 bg-[#151921] relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B4A]/10 border border-[#FF6B4A]/20 text-[#FF6B4A] mb-8">
                <span className="material-symbols-outlined text-lg">local_fire_department</span>
                <span className="text-xs font-bold tracking-widest uppercase">Next Cohort Starts Feb 2026</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Transform Your PMM Career in <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B4A] to-[#8B5CF6]">9 Weeks</span> 
              </h2>
              <p className="text-lg text-slate-400 mb-10 leading-relaxed max-w-xl">
                Join our intensive cohort program designed to accelerate your growth through structured learning, peer collaboration, and expert mentorship.
              </p>
              <div className="space-y-6 mb-12">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                    <span className="material-symbols-outlined text-lg font-bold">check</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Limited to 30 Members</h4>
                    <p className="text-slate-400 text-sm">Intimate cohort size ensures personalized attention and deep connections.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                    <span className="material-symbols-outlined text-lg font-bold">check</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Lifetime Community Access</h4>
                    <p className="text-slate-400 text-sm">Continue learning and networking long after your cohort ends.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#25D366]/20 flex items-center justify-center text-[#25D366]">
                    <span className="material-symbols-outlined text-lg font-bold">check</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Real-World Capstone Project</h4>
                    <p className="text-slate-400 text-sm">Build a complete GTM strategy for a real company to showcase in your portfolio.</p>
                  </div>
                </div>
              </div>
              <Link href="/cohort" className="px-8 py-4 bg-gradient-to-r from-[#FF6B4A] to-[#FF6B4A]/80 text-white rounded-2xl font-bold shadow-xl shadow-[#FF6B4A]/20 hover:scale-105 transition-all flex items-center gap-2 inline-flex">
                View Curriculum & Details <span className="material-symbols-outlined">arrow_right_alt</span>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-10 bg-[#8B5CF6]/20 blur-[120px] rounded-full opacity-50"></div>
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/20 rounded-[2.5rem] p-8 overflow-hidden shadow-2xl">
                <AppImage 
                  src="/assets/images/Gemini_Generated_Image_zdi8f7zdi8f7zdi8-1770228701449.png" 
                  alt="PMM strategy framework showing circular flow between Foundations, Strategy, Execution, and Growth phases" 
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Community CTA */}
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#FF6B4A]/20 to-[#8B5CF6]/20 border border-white/10 p-12 md:p-20 rounded-[3rem] text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF6B4A]/20 blur-[100px] rounded-full"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-8 tracking-tight">Don't journey alone.</h2>
            <p className="text-slate-300 text-lg mb-12 max-w-xl mx-auto">Access real-time discussions, job alerts, and peer feedback in our exclusive WhatsApp group.</p>
            <button className="px-10 py-5 bg-[#25D366] text-[#101822] text-xl font-extrabold rounded-2xl shadow-2xl shadow-[#25D366]/30 hover:scale-105 transition-all flex items-center justify-center gap-3 mx-auto">
              <span className="material-symbols-outlined text-3xl">forum</span>
              Join WhatsApp Community
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <Link href="/homepage" className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-lg overflow-hidden">
              <Image
                src="/assets/images/C_-_GoPMM_2_-_Square-1770356625482.png"
                alt="GoPMM Community Logo"
                fill
                className="object-contain"
              />
            </div>
            <span className="font-display font-bold text-xl">GoPMM</span>
          </Link>
          <div className="flex flex-wrap justify-center gap-8 text-sm font-semibold text-slate-500">
            <a className="hover:text-[#FF6B4A] transition-colors" href="https://www.linkedin.com/company/gopmm/">LinkedIn</a>
            <a className="hover:text-[#FF6B4A] transition-colors" href="mailto:varun@gopmm.in">Contact Us</a>
          </div>
          <p className="text-sm text-slate-500">© 2024 GoPMM Community. Built for India.</p>
        </div>
      </footer>
    </div>
  );
}